import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import AncillaryPersonSummary from 'components/Campaign/AncillaryPersonSummary';
import EmptyResults from 'components/Campaign/EmptyResults';
import { PetitionTargetRollup } from 'components/cards';
import Layout from 'components/layout';
import Anchor from 'components/typography/anchor';
import { getRepsWithPetitionSigns } from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import { getOpenGraphUrl } from 'utilities/paths';
import { hasSufficientPollData } from 'utilities/polls';
import { AncillaryPersonType, CampaignTypePerson } from 'utilities/propTypes';
import {
  getStateProperName,
  isValidStateCode,
  STATE_POSTAL_CODES,
} from 'utilities/states';

const REVALIDATE_TIME_MINUTES = 10;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;

const PetitionsForState = ({
  representatives,
  ancillaryRepresentatives,
  stateCode,
}) => {
  const representativeSection = representatives.map((r, idx) => (
    <PetitionTargetRollup
      key={`${r.bioguideId}-${r.districtId}-${r.fullName}`}
      stateCode={stateCode}
      bioguideId={r.bioguideId}
      districtId={r.districtId}
      fullName={r.fullName}
      officialType={r.officialType}
      deliveryTotal={r.intervalTotal}
      approvalRating={r.polls?.items?.[0]?.approval || null}
      hideApprovalRating={
        !hasSufficientPollData({
          up: r.polls?.items?.[0]?.up,
          down: r.polls?.items?.[0]?.down,
        })
      }
      idx={idx}
    />
  ));

  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/explore/state', {
    state: stateCode,
  });

  const imageAltText = `Letters delivered to elected officials in ${getStateProperName(
    stateCode
  )}.`;

  return (
    <Layout
      title={`${getStateProperName(stateCode)} Officials — Petitions & Letters | Resistbot`}
      description={`Elected officials in ${getStateProperName(stateCode)} contacted by Resistbot members`}
      imageAltText={imageAltText}
      dynamicImage={dynamicOpenGraphUrlHref}
      canonicalPath={`/petitions/explore/${stateCode}`}
    >
      <div>
        <ol
          aria-label="breadcrumbs"
          className="flex gap-2 text-sm tracking-wider uppercase"
        >
          <li className="after:content-['→']">
            <Anchor className="mr-2 anchor-subtle" href="/petitions">
              United States
            </Anchor>
          </li>
          <li>{getStateProperName(stateCode)}</li>
        </ol>
        <h1 className="mb-4">
          {getStateProperName(stateCode)} Elected Officials
        </h1>
      </div>

      {/* Currently this displays _all_ representatives we have petition signs for (in this state). */}
      {representatives.length < 1 ? (
        <EmptyResults type={DATA_TYPES.PETITION} />
      ) : (
        <div className="flex flex-col space-y-1">{representativeSection}</div>
      )}
      <AncillaryPersonSummary
        groupedRepresentatives={ancillaryRepresentatives}
        stateCode={stateCode}
      />
    </Layout>
  );
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params }) => {
    const stateCode = params.statecode.toUpperCase();

    const parentSpan = otelApi.trace.getActiveSpan();
    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/petitions/explore/${stateCode}`,
      'state.code': stateCode,
    });

    if (!isValidStateCode(stateCode)) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    const { representativesUpper, representativesLower } =
      await getRepsWithPetitionSigns({
        stateCode,
      });

    parentSpan.end();

    // Group the cleaned ancillary representatives by type
    const ancillaryByTypeKey = {};
    representativesLower
      .map((r) => ({
        id: r.bioguideId,
        name: r.fullName,
        type: r.officialType,
        location: r.districtId,
      }))
      .sort((a, b) => {
        if (a.location === b.location) {
          return 0;
        }
        return a.location < b.location ? -1 : 1;
      })
      .forEach((r) => {
        if (ancillaryByTypeKey[r.type]) {
          ancillaryByTypeKey[r.type].push(r);
        } else {
          ancillaryByTypeKey[r.type] = [r];
        }
      });

    return {
      props: {
        representatives: representativesUpper,
        ancillaryRepresentatives: ancillaryByTypeKey,
        stateCode,
      },
      revalidate: REVALIDATE_TIME_SECONDS,
    };
  }
);

PetitionsForState.propTypes = {
  representatives: PropTypes.arrayOf(CampaignTypePerson),
  ancillaryRepresentatives: PropTypes.shape({
    state_upper: PropTypes.arrayOf(AncillaryPersonType),
    state_lower: PropTypes.arrayOf(AncillaryPersonType),
    mayor: PropTypes.arrayOf(AncillaryPersonType),
  }).isRequired,
  stateCode: PropTypes.oneOf(Array.from(STATE_POSTAL_CODES)),
};

export default PetitionsForState;
